import React from "react";
import { useNavigate } from "react-router-dom";

interface CheckoutButtonProps {
  plan: string;
  customClass?: string;
  onCheckout?: (plan: string) => void; // Handler passed from PricingPage
  isLoading?: boolean; // Loading state passed from PricingPage
}

const CheckoutButton: React.FC<CheckoutButtonProps> = ({
  plan,
  customClass = "",
  onCheckout,
  isLoading = false,
}) => {
  const navigate = useNavigate();
  
  // If onCheckout is not provided, navigate to pricing page
  const handleClick = () => {
    if (onCheckout) {
      onCheckout(plan);
    } else {
      // Navigate to pricing page with plan preference
      const isYearly = plan.includes("yearly");
      navigate(`/pricing?plan=${isYearly ? 'yearly' : 'monthly'}`);
    }
  };
  const getPlanAction = () => {
    // We now have a single plan so no need to differentiate
    if (plan.includes("monthly")) return "Start Monthly";
    else if (plan.includes("yearly")) return "Start Yearly";
    return "Get Started";
  };

  return (
    <button
      className={`checkout-button ${customClass} ${isLoading ? "loading" : ""}`}
      onClick={handleClick} // Use our new handleClick function
      disabled={isLoading}
    >
      {isLoading ? (
        <div className="button-loader">
          <div className="orbit-animation">
            <div className="planet"></div>
            <div className="orbit"></div>
          </div>
        </div>
      ) : (
        <>
          <span className="button-text">{getPlanAction()}</span>
          <span className="button-icon">🚀</span>
        </>
      )}
      <style>
        {`
          .checkout-button {
            padding: 12px 24px;
            font-size: 16px;
            font-weight: bold;
            color: #ffffff;
            background: ${
              customClass
                ? "linear-gradient(45deg, #6366F1, #A78BFA)"
                : "linear-gradient(45deg, #4B5563, #6B7280)"
            };
            border: none;
            border-radius: 8px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            transition: transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease;
            box-shadow: ${
              customClass
                ? "0 0 10px rgba(99, 102, 241, 0.5)"
                : "0 0 10px rgba(75, 85, 99, 0.5)"
            };
            width: 100%;
          }

          .checkout-button:hover:not(:disabled) {
            transform: scale(1.05);
            box-shadow: ${
              customClass
                ? "0 0 15px rgba(99, 102, 241, 0.8)"
                : "0 0 15px rgba(75, 85, 99, 0.8)"
            };
            background: ${
              customClass
                ? "linear-gradient(45deg, #4f46e5, #8b5cf6)"
                : "linear-gradient(45deg, #374151, #4b5563)"
            };
          }

          .checkout-button:disabled {
            cursor: not-allowed;
            opacity: 0.6;
          }

          .button-text {
            font-size: 16px;
          }

          .button-icon {
            font-size: 18px;
            animation: pulse 2s infinite;
          }

          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.2); opacity: 0.8; }
          }

          .button-loader {
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .orbit-animation {
            position: relative;
            width: 24px;
            height: 24px;
          }

          .planet {
            position: absolute;
            top: 50%;
            left: 50%;
            width: 8px;
            height: 8px;
            background: #ffffff;
            border-radius: 50%;
            transform: translate(-50%, -50%);
            box-shadow: 0 0 8px rgba(255, 255, 255, 0.8);
          }

          .orbit {
            position: absolute;
            top: 50%;
            left: 50%;
            width: 24px;
            height: 24px;
            border: 2px dashed rgba(255, 255, 255, 0.5);
            border-radius: 50%;
            transform: translate(-50%, -50%);
            animation: rotate-orbit 2s linear infinite;
          }

          @keyframes rotate-orbit {
            0% { transform: translate(-50%, -50%) rotate(0deg); }
            100% { transform: translate(-50%, -50%) rotate(360deg); }
          }
        `}
      </style>
    </button>
  );
};

export default CheckoutButton;
